const crudEndpoints = (base) => {
    return {
        getItems: {
            path: "/api/v2/" + base,
            method: "get",
            requiresAuth: true,
        },
        getItemCount: {
            path: "/api/v2/" + base + "/count",
            method: "get",
            requiresAuth: true,
        },
        getItem: (id) => {
            return {
                path: "/api/v2/" + base + "/" + id,
                method: "get",
                requiresAuth: true,
            }
        },
        deleteItem: (id) => {
            return {
                path: "/api/v2/" + base + "/" + id,
                method: "delete",
                requiresAuth: true,
            }
        },
        updateItem: (id) => {
            return {
                path: "/api/v2/" + base + "/" + id,
                method: "put",
                requiresAuth: true,
            }
        },
        resendInvite: (id, clubId) => {
            return {
                path: "/api/v2/" + base + "/" + id + "/resend/" + clubId,
                method: "get",
                requiresAuth: true,
            }
        },
        addItem: {
            path: "/api/v2/" + base,
            method: "post",
            requiresAuth: true,
        }
    }
}

// The place search scope as a query parameter: the server's placeScope when a
// field carries one, else the regionsOnly flag every server understands.
const placeScopeParam = (scope) => (typeof scope === 'string' && scope.trim()
    ? "scope=" + encodeURIComponent(scope.trim())
    : "regionsOnly=true");

export const endpoints = {
    timeline: {
        sync: {
            path: "/api/v2/timeline",
            method: "get",
            requiresAuth: true,
        },
        dismiss: (id) => {
            return {
                path: `/api/v2/timeline/${id}`,
                method: "delete",
                requiresAuth: true,
            }
        },
        feed: {
            path: "/api/v2/timeline/feed",
            method: "get",
            requiresAuth: true,
        },
        feedDismiss: {
            path: "/api/v2/timeline/feed/dismiss",
            method: "post",
            requiresAuth: true,
        },
        liveNow: {
            path: "/api/v2/timeline/livenow",
            method: "get",
            requiresAuth: true,
        }
    },
    contact: {
        message: {
            path: "/api/v2/contact",
            method: "post",
            requiresAuth: false,
        }
    },
    // Email preferences (29 Sep 2026). The tokenised page from a link in an
    // email needs nobody signed in; the same page and the profile's Club emails
    // section use the signed-in pair. The token rides in the query on the PUT
    // too, because httpRequest only builds a query string for a GET.
    emailPreferences: {
        byToken: (token, type) => ({
            path: "/api/v2/emailPreferences?t=" + encodeURIComponent(token) +
                (type != null ? "&type=" + encodeURIComponent(type) : ""),
            method: "get",
            requiresAuth: false,
        }),
        updateByToken: (token) => ({
            path: "/api/v2/emailPreferences?t=" + encodeURIComponent(token),
            method: "put",
            requiresAuth: false,
        }),
        mine: (type) => ({
            path: "/api/v2/auth/me/emailPreferences" + (type != null ? "?type=" + encodeURIComponent(type) : ""),
            method: "get",
            requiresAuth: true,
        }),
        updateMine: {path: "/api/v2/auth/me/emailPreferences", method: "put", requiresAuth: true},
    },
    auth: {
        login: {
            path: "/api/v2/auth/login",
            method: "post",
            requiresAuth: false,
        },
        // A member combining their own two accounts: prove the other by email + password
        // or by a pending identity token (ExternalLogin intent "prove"). Preview first.
        combineAccountsPreview: {path: "/api/v2/auth/combine-accounts/preview", method: "post", requiresAuth: true},
        combineAccounts: {path: "/api/v2/auth/combine-accounts", method: "post", requiresAuth: true},
        register: {
            path: "/api/v2/auth/me",
            method: "post",
            requiresAuth: false,
        },
        forgotPassword: {
            path: "/api/v2/auth/forgot-password",
            method: "post",
            requiresAuth: false,
        },
        validateShortLivedToken: (token) => {
            return {
                path: "/api/v2/auth/short-lived-token-validate",
                method: "get",
                requiresAuth: false,
            }
        },
        validateToken: (token) => {
            return {
                path: "/api/v2/auth/validate/" + token,
                method: "get",
                requiresAuth: false,
            }
        },
        acceptInvite: (token, data) => {
            return {
                path: "/api/v2/auth/validate/" + token + "/accept",
                method: data ? "post" : "get",
                requiresAuth: false,
            }
        },
        rejectInvite: (token) => {
            return {
                path: "/api/v2/auth/validate/" + token + "/accept",
                method: "delete",
                requiresAuth: false,
            }
        },
        createPassword: {
            path: "/api/v2/auth/validate",
            method: "post",
            requiresAuth: false,
        },
        me: {
            path: "/api/v2/auth/me",
            method: "get",
            requiresAuth: true,
        },
        meUpdate: {
            path: "/api/v2/auth/me",
            method: "put",
            requiresAuth: true,
        },
        meDelete: {
            path: "/api/v2/auth/me",
            method: "delete",
            requiresAuth: true,
        },
        mePassword: {
            path: "/api/v2/auth/me/password",
            method: "put",
            requiresAuth: true,
        },
        completeSetup: {path: "/api/v2/auth/me/completeSetup", method: "post", requiresAuth: true},
        // The profile's "About you" section: the live sign-up set's data fields
        // with the account's current values. Saved through meUpdate's `details`.
        meDetails: {path: "/api/v2/auth/me/details", method: "get", requiresAuth: true},
        meServices: {
            path: "/api/v2/auth/me/externalServices",
            method: "put",
            requiresAuth: true,
        },
        // The Privacy tab (PRIVACY_PLAN.md §3.3, §3.5): level, leaderboard opt-in and label, saved as one panel.
        mePrivacy: {
            path: "/api/v2/auth/me/privacy",
            method: "put",
            requiresAuth: true,
        },
        meNotificationPreferencesGet: {
            path: "/api/v2/auth/me/notificationPreferences",
            method: "get",
            requiresAuth: true,
        },
        meNotificationPreferencesUpdate: {
            path: "/api/v2/auth/me/notificationPreferences",
            method: "put",
            requiresAuth: true,
        },
        meNotificationPreferencesReset: {
            path: "/api/v2/auth/me/notificationPreferences",
            method: "delete",
            requiresAuth: true,
        },
        mePicture: {
            path: "/api/v2/auth/me/picture",
            method: "put",
            requiresAuth: true,
        }
    },
    uploads: {
        getItems: {
            path: "/api/v2/uploads",
            method: "get",
            requiresAuth: true,
        },
        getItem: (id, size) => {
            return {
                path: "/api/v2/uploads/" + id + "/" + size,
                method: "get",
                requiresAuth: true,
            }
        },
        addItem: {
            path: "/api/v2/uploads",
            method: "post",
            requiresAuth: true,
        }
    },
    pendingUsers: {
        getItems: (clubId) => {
            return {
                path: "/api/v2/users/pending/club/" + clubId,
                method: "get",
                requiresAuth: true,
            }
        },
        deleteItem: (pendingUserId) => {
            return {
                path: "/api/v2/users/pending/" + pendingUserId,
                method: "delete",
                requiresAuth: true,
            }
        }
    },
    teams: {
        ...crudEndpoints("teams"),
    },
    players: {
        ...crudEndpoints("players"),
        deleteFromClub: (playerId, clubId) => {
            return {
                path: "/api/v2/players/" + playerId + "/club/" + clubId,
                method: "delete",
                requiresAuth: true,
            }
        }
    },
    howTos: {
        ...crudEndpoints("howTos"),
        // Super admin only — publishes the assistant's help-docs corpus straight from the
        // docs repo, replacing the local publish-docs.py script. Always preview with
        // dryRun: true before publishing for real.
        publishFromRepo: {path: "/api/v2/howTos/publishFromRepo", method: "post", requiresAuth: true},
        publishStatus: {path: "/api/v2/howTos/publishStatus", method: "get", requiresAuth: true},
    },
    howToCategories: {
        ...crudEndpoints("howToCategories"),
    },
    howToKeywords: {
        ...crudEndpoints("howToKeywords"),
    },
    accessApplications: {
        ...crudEndpoints("accessApplications"),
    },
    users: {
        ...crudEndpoints("users"),
        getPicture: (id, size) => {
            return {
                path: "/api/v2/users/" + id + "/picture/" + size,
                method: "get",
                requiresAuth: true,
            }
        },
        getRequest: (id) => {
            return {
                path: "/api/v2/users/requests/" + id,
                method: "get",
                requiresAuth: true
            }
        },
        approveRequest: {
            path: "/api/v2/users/requests",
            method: "put",
            requiresAuth: true
        },
        getRequests: {
            path: "/api/v2/users/requests",
            method: "get",
            requiresAuth: true,
        },
        addRequest: {
            path: "/api/v2/users/requests",
            method: "post",
            requiresAuth: true,
        },
        cancelRequest: (id) => {
            return {
                path: "/api/v2/users/requests/" + id,
                method: "delete",
                requiresAuth: true,
            }
        },
        mail: {
            path: "/api/v2/users/mail",
            method: "post",
            requiresAuth: true,
        },
        push: {
            path: "/api/v2/users/push",
            method: "post",
            requiresAuth: true,
        },
        getPendingItems: {
            path: "/api/v2/users/pending",
            method: "get",
            requiresAuth: true,
        },
        deletePendingItem: (id) => {
            return {
                path: "/api/v2/users/pending/" + id,
                method: "delete",
                requiresAuth: true,
            }
        },
        impersonate: (id) => {
            return {
                path: "/api/v2/users/" + id + "/impersonate",
                method: "get",
                requiresAuth: true,
            }
        },
    },
    clubs: {
        ...crudEndpoints("clubs"),
        paymentControls: (clubId, save = false) => {
            // super admin staged-rollout controls: payments UI toggle + commission override
            return {path: "/api/v2/clubs/" + clubId + "/paymentControls", method: save ? "put" : "get", requiresAuth: true}
        },
        logo: (clubId) => {
            return {
                path: "/api/v2/clubs/" + clubId + "/logo",
                method: "put",
                requiresAuth: true,
            }
        },
        leave: (clubId) => {
            return {
                path: "/api/v2/clubs/" + clubId + "/leave",
                method: "delete",
                requiresAuth: true,
            }
        },
        toDo: {
            path: "/api/v2/clubs/todo",
            method: "get",
            requiresAuth: true,
        }
    },
    leagues: {
        ...crudEndpoints("leagues"),
    },
    // ── Americano leagues (AmericanosController + AmericanoCyclesController) ──
    americanos: {
        ...crudEndpoints("americanos"),
        unarchive: (americanoId) => {
            return {
                path: "/api/v2/americanos/" + americanoId + "/unarchive",
                method: "post",
                requiresAuth: true,
            }
        },
    },
    americanoCycles: {
        ...crudEndpoints("americanoCycles"),
        rolloverPreview: (id, change) => {
            return {
                path: "/api/v2/americanoCycles/" + id + "/rollover-preview?change=" + change,
                method: "get",
                requiresAuth: true,
            }
        },
        swapPlayer: (cycleId, divisionId) => {
            return {
                path: "/api/v2/americanoCycles/" + cycleId + "/divisions/" + divisionId + "/swap-player",
                method: "post",
                requiresAuth: true,
            }
        },
        unarchive: (cycleId) => {
            return {
                path: "/api/v2/americanoCycles/" + cycleId + "/unarchive",
                method: "post",
                requiresAuth: true,
            }
        },
        // Streamed PDF (application/pdf), never stored. Club admins and super admins
        // only (403 otherwise). Fetch with the bearer token (useFileDownload.fetchFile).
        pdfFile: (id, {juniorFullNames = false} = {}) => ({
            path: `/api/v2/americanoCycles/${id}/pdf/file?juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
        // Page images of the same PDF, for the export dialog's preview (never the PDF).
        pdfPreview: (id, {juniorFullNames = false} = {}) => ({
            path: `/api/v2/americanoCycles/${id}/pdf/preview?juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
    },
    // ── Meets (MeetsController + MeetOccurrencesController, member surfaces) ──
    // Backend-directed "Are you enjoying Tap Leagues?" prompt — super admin controls + answers
    // The published Terms of Use and Privacy Policy. The two reads carry no
    // auth on purpose: a member held by a required terms prompt has to be able
    // to READ the document the wall is asking about, and so does a signed-out
    // visitor deciding whether to sign up.
    legalDocuments: {
        current: {path: "/api/v2/legalDocuments/current", method: "get", requiresAuth: false},
        get: (key) => ({path: "/api/v2/legalDocuments/" + key, method: "get", requiresAuth: false}),
        editions: {path: "/api/v2/legalDocuments/editions", method: "get", requiresAuth: true},
        publishPreview: {path: "/api/v2/legalDocuments/publishPreview", method: "get", requiresAuth: true},
        publish: {path: "/api/v2/legalDocuments/publish", method: "post", requiresAuth: true},
    },
    userFormPrompts: {
        pending: {path: "/api/v2/userFormPrompts/pending", method: "get", requiresAuth: true},
        submit: (id) => ({path: "/api/v2/userFormPrompts/" + id + "/submit", method: "post", requiresAuth: true}),
        dismiss: (id) => ({path: "/api/v2/userFormPrompts/" + id + "/dismiss", method: "post", requiresAuth: true}),
        // "Not now". Live on the API since the feature shipped and never once
        // called by a client, which faked it with per-session state instead.
        snooze: (id) => ({path: "/api/v2/userFormPrompts/" + id + "/snooze", method: "post", requiresAuth: true}),
        list: {path: "/api/v2/userFormPrompts", method: "get", requiresAuth: true},
        get: (id) => ({path: "/api/v2/userFormPrompts/" + id, method: "get", requiresAuth: true}),
        create: {path: "/api/v2/userFormPrompts", method: "post", requiresAuth: true},
        update: (id) => ({path: "/api/v2/userFormPrompts/" + id, method: "put", requiresAuth: true}),
        remove: (id) => ({path: "/api/v2/userFormPrompts/" + id, method: "delete", requiresAuth: true}),
        stats: (id) => ({path: "/api/v2/userFormPrompts/" + id + "/stats", method: "get", requiresAuth: true}),
        export: (id) => ({path: "/api/v2/userFormPrompts/" + id + "/export", method: "get", requiresAuth: true}),
        publish: {path: "/api/v2/userFormPrompts/publish", method: "post", requiresAuth: true},
        bindings: {path: "/api/v2/userFormPrompts/bindings", method: "get", requiresAuth: true},
        assists: {path: "/api/v2/userFormPrompts/assists", method: "get", requiresAuth: true},
    },
    // Two-step sign-in (MFA_PLAN.md). Reachable before this session has verified.
    mfa: {
        status: {path: "/api/v2/auth/mfa", method: "get", requiresAuth: true},
        beginAuthenticator: {path: "/api/v2/auth/mfa/authenticator/begin", method: "post", requiresAuth: true},
        confirmAuthenticator: {path: "/api/v2/auth/mfa/authenticator/confirm", method: "post", requiresAuth: true},
        beginPasskey: {path: "/api/v2/auth/mfa/passkey/begin", method: "post", requiresAuth: true},
        confirmPasskey: {path: "/api/v2/auth/mfa/passkey/confirm", method: "post", requiresAuth: true},
        passkeyAssertion: {path: "/api/v2/auth/mfa/passkey/assertion", method: "post", requiresAuth: true},
        verify: {path: "/api/v2/auth/mfa/verify", method: "post", requiresAuth: true},
        removeMethod: (id) => ({path: `/api/v2/auth/mfa/methods/${id}`, method: "delete", requiresAuth: true}),
        devices: {path: "/api/v2/auth/mfa/devices", method: "get", requiresAuth: true},
        revokeDevice: (id) => ({path: `/api/v2/auth/mfa/devices/${id}`, method: "delete", requiresAuth: true}),
        recoveryCodes: {path: "/api/v2/auth/mfa/recoveryCodes", method: "post", requiresAuth: true},
    },
    // Sign in with a passkey alone, no password (MFA_PLAN.md). Open: nobody is signed in yet.
    passkeySignIn: {
        options: {path: "/api/v2/auth/passkey/options", method: "post", requiresAuth: false},
        login: {path: "/api/v2/auth/passkey/login", method: "post", requiresAuth: false},
    },
    mfaAdmin: {
        policy: {path: "/api/v2/admin/mfa/policy", method: "get", requiresAuth: true},
        setPolicy: {path: "/api/v2/admin/mfa/policy", method: "put", requiresAuth: true},
        resetUser: (userId) => ({path: `/api/v2/admin/mfa/users/${userId}`, method: "delete", requiresAuth: true}),
    },
    reviewPrompt: {
        settings: {path: "/api/v2/reviewPrompt/settings", method: "get", requiresAuth: true},
        updateSettings: {path: "/api/v2/reviewPrompt/settings", method: "put", requiresAuth: true},
        preview: {path: "/api/v2/reviewPrompt/preview", method: "get", requiresAuth: true},
        responses: (page = 1, pageSize = 50, feedbackOnly = false) => {
            return {path: `/api/v2/reviewPrompt/responses?page=${page}&pageSize=${pageSize}&feedbackOnly=${feedbackOnly}`, method: "get", requiresAuth: true}
        },
    },
    // Bill to requests, the payer's side (C5-1): signed in, payer only
    meetBillingRequests: {
        mine: {path: "/api/v2/meetBillingRequests/mine", method: "get", requiresAuth: true},
        get: (token) => ({path: "/api/v2/meetBillingRequests/" + token, method: "get", requiresAuth: true}),
        respond: (token) => ({path: "/api/v2/meetBillingRequests/" + token + "/respond", method: "post", requiresAuth: true}),
    },
    meetOffers: {
        // Anonymous — the mailed token is the auth. What the offer is, before answering.
        summary: (token) => {
            return {path: "/api/v2/meetOffers/" + token, method: "get", requiresAuth: false}
        },
    },
    meets: {
        ...crudEndpoints("meets"),
        leave: (meetId) => {
            return {
                path: "/api/v2/meets/" + meetId + "/leave",
                method: "delete",
                requiresAuth: true,
            }
        },
        acceptTerms: (meetId) => {
            return {
                path: "/api/v2/meets/" + meetId + "/acceptTerms",
                method: "post",
                requiresAuth: true,
            }
        },
        // Admin: what saving a new fee would do to upcoming dates + who must reconfirm
        priceChangeImpact: (meetId, feeMinor) => {
            const q = feeMinor == null ? "" : "?feeMinor=" + feeMinor;
            return {path: "/api/v2/meets/" + meetId + "/priceChangeImpact" + q, method: "get", requiresAuth: true}
        },
        // Admin: what saving the payment settings does to what members agreed to
        // (price, terms, cancellation charges, when charged, notice). Body = the save payload.
        agreedChangeImpact: (meetId) => ({path: "/api/v2/meets/" + meetId + "/agreedChangeImpact", method: "post", requiresAuth: true}),
        // ── Admin (MEETS_SCREENS.md B1) ──
        // Admin: the club's players in effective skill order for a meet (saved
        // order merged with results) — and the PUT that replaces the saved order.
        skillOrder: (id) => ({path: "/api/v2/meets/" + id + "/skillOrder", method: "get", requiresAuth: true}),
        saveSkillOrder: (id) => ({path: "/api/v2/meets/" + id + "/skillOrder", method: "put", requiresAuth: true}),
        billing: (id) => {
            // per-meet payments page: periods + members + charge history
            return {path: "/api/v2/meets/" + id + "/billing", method: "get", requiresAuth: true}
        },
        // Bill a player's sessions to another club member — body {playerId, billToUserId|null}
        assignBilling: (id) => {
            return {path: "/api/v2/meets/" + id + "/billingAssignments", method: "post", requiresAuth: true}
        },
        // The meet's live "someone else pays" rows — badges admin surfaces
        billingAssignments: (id) => {
            return {path: "/api/v2/meets/" + id + "/billingAssignments", method: "get", requiresAuth: true}
        },
        // Email a Pending or Declined Bill to request to the payer again (C5-1)
        resendBillingRequest: (id, playerId) => ({
            path: "/api/v2/meets/" + id + "/billingAssignments/" + playerId + "/resend", method: "post", requiresAuth: true
        }),
        unarchive: (id) => {
            return {path: "/api/v2/meets/" + id + "/unarchive", method: "post", requiresAuth: true}
        },
        // Admin-curated roster (every meet has one) — body {playerId}; both return the meet detail
        addPlayer: (id) => {
            return {path: "/api/v2/meets/" + id + "/players", method: "post", requiresAuth: true}
        },
        removePlayer: (id, playerId) => {
            return {path: "/api/v2/meets/" + id + "/players/" + playerId, method: "delete", requiresAuth: true}
        },
    },
    meetOccurrences: {
        ...crudEndpoints("meetOccurrences"),
        // body: { attending: true|false }
        rsvp: (occurrenceId) => {
            return {
                path: "/api/v2/meetOccurrences/" + occurrenceId + "/rsvp",
                method: "post",
                requiresAuth: true,
            }
        },
        // Accept an offered spot. Needs the current terms, never a saved card:
        // a card-less acceptance settles offline (D122). Body {acceptTerms}.
        confirmPromotion: (occurrenceId) => ({
            path: "/api/v2/meetOccurrences/" + occurrenceId + "/promotion/confirm", method: "post", requiresAuth: true,
        }),
        cancelRsvp: (occurrenceId) => {
            return {
                path: "/api/v2/meetOccurrences/" + occurrenceId + "/rsvp",
                method: "delete",
                requiresAuth: true,
            }
        },
        // ── Admin event-day console (MEETS_SCREENS.md B2/B4) ──
        // Put a cancelled future date back on — RSVPs stand, attendees notified.
        restore: (id) => {
            return {
                path: "/api/v2/meetOccurrences/" + id + "/restore",
                method: "post",
                requiresAuth: true,
            }
        },
        // Move ONE date to a different time — keeps its slot in the recurrence
        // rule (no duplicate on the vacated time) and notifies everyone active.
        reschedule: (id) => {
            return {
                path: "/api/v2/meetOccurrences/" + id + "/reschedule",
                method: "post",
                requiresAuth: true,
            }
        },
        // Cancel this date (or this and every future scheduled date) — distinct from DELETE.
        cancel: (id, applyToFuture) => {
            return {
                path: "/api/v2/meetOccurrences/" + id + "/cancel?applyToFuture=" + (applyToFuture ? "true" : "false"),
                method: "post",
                requiresAuth: true,
            }
        },
        // Admin walk-in — body {playerId, walkIn: true}
        addAttendance: (id) => {
            return {path: "/api/v2/meetOccurrences/" + id + "/attendances", method: "post", requiresAuth: true}
        },
        // Register correction inside the grace window — body {status, waive}
        correctAttendance: (id, attendanceId) => {
            return {
                path: "/api/v2/meetOccurrences/" + id + "/attendances/" + attendanceId,
                method: "put",
                requiresAuth: true,
            }
        },
        // Admin removes someone from the register (free; offers the spot to the waitlist)
        removeAttendance: (id, attendanceId) => {
            return {
                path: "/api/v2/meetOccurrences/" + id + "/attendances/" + attendanceId,
                method: "delete",
                requiresAuth: true,
            }
        },
        // Fixture generation preview (nothing persists) — body {generationMode?, fixtureCount?, seed?}
        generateFixtures: (id) => {
            return {path: "/api/v2/meetOccurrences/" + id + "/fixtures/generate", method: "post", requiresAuth: true}
        },
        // Confirm the (possibly edited) pairings — body {fixtureCount?, rounds: [{fixtures: [{homePlayerIds, awayPlayerIds}]}]};
        // fixtureCount persists as the occurrence's fixtureCountOverride
        // One more match on a date whose match-ups exist (a late joiner) — the
        // rest, and their results, stay as they are.
        addFixture: (id) => ({path: "/api/v2/meetOccurrences/" + id + "/fixtures/add", method: "post", requiresAuth: true}),
        // Remove every (unplayed) match — the date goes back to "not made yet"
        clearFixtures: (id) => ({path: "/api/v2/meetOccurrences/" + id + "/fixtures/clear", method: "post", requiresAuth: true}),
        confirmFixtures: (id) => {
            return {path: "/api/v2/meetOccurrences/" + id + "/fixtures/confirm", method: "post", requiresAuth: true}
        },
        finalise: (id) => {
            return {path: "/api/v2/meetOccurrences/" + id + "/finalise", method: "post", requiresAuth: true}
        },
        refundAll: (id) => {
            return {path: "/api/v2/meetOccurrences/" + id + "/refundAll", method: "post", requiresAuth: true}
        },
    },
    ladders: {
        ...crudEndpoints("ladders"),
        leave: (ladderId) => {
            return {
                path: "/api/v2/ladders/" + ladderId + "/leave",
                method: "delete",
                requiresAuth: true,
            }
        },
        unfreeze: (ladderId) => {
            return {
                path: "/api/v2/ladders/" + ladderId + "/unfreeze",
                method: "put",
                requiresAuth: true,
            }
        },
        // Streamed PDF (application/pdf), never stored. Club admins and super admins
        // only (403 otherwise). Fetch with the bearer token (useFileDownload.fetchFile).
        pdfFile: (id, {juniorFullNames = false} = {}) => ({
            path: `/api/v2/ladders/${id}/pdf/file?juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
        // Page images of the same PDF, for the export dialog's preview (never the PDF).
        pdfPreview: (id, {juniorFullNames = false} = {}) => ({
            path: `/api/v2/ladders/${id}/pdf/preview?juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
    },
    ladderChallenges: {
        ...crudEndpoints("ladderChallenges"),
        // the external services a challenge would carry, before it is sent (the Send challenge form)
        externalServicesPreview: {path: "/api/v2/ladderChallenges/externalServicesPreview", method: "get", requiresAuth: true}
    },
    // Team handicaps (HANDICAPS_PLAN.md): per-competition settings, the scheme catalog +
    // preview, and the append-only change history.
    handicapSettings: {
        get: {path: "/api/v2/handicapsettings", method: "get", requiresAuth: true},
        set: {path: "/api/v2/handicapsettings", method: "put", requiresAuth: true},
        // the start two teams would play off before their fixture exists (record-result dialog)
        start: {path: "/api/v2/handicapsettings/start", method: "get", requiresAuth: false},
    },
    handicapSchemes: {
        list: {path: "/api/v2/handicapschemes", method: "get", requiresAuth: false},
        preview: {path: "/api/v2/handicapschemes/preview", method: "post", requiresAuth: false},
    },
    handicapChanges: {
        ...crudEndpoints("handicapChanges"),
        batch: {path: "/api/v2/handicapChanges/batch", method: "post", requiresAuth: true},
    },
    tournaments: {
        ...crudEndpoints("tournaments"),
        // Streamed PDF (application/pdf), never stored. Club admins and super admins
        // only (403 otherwise). Fetch with the bearer token (useFileDownload.fetchFile).
        pdfFile: (id, {juniorFullNames = false} = {}) => ({
            path: `/api/v2/tournaments/${id}/pdf/file?juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
        // Page images of the same PDF, for the export dialog's preview (never the PDF).
        pdfPreview: (id, {juniorFullNames = false} = {}) => ({
            path: `/api/v2/tournaments/${id}/pdf/preview?juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
        generateDraw: (id) => {
            return {
                path: "/api/v2/tournaments/" + id + "/generate-draw",
                method: "post",
                requiresAuth: true,
            }
        },
        regenerateDraw: (id) => {
            return {
                path: "/api/v2/tournaments/" + id + "/regenerate-draw",
                method: "post",
                requiresAuth: true,
            }
        },
        clearDraw: (id) => {
            return {
                path: "/api/v2/tournaments/" + id + "/clear-draw",
                method: "post",
                requiresAuth: true,
            }
        },
        progressFromGroups: (id) => {
            return {
                path: "/api/v2/tournaments/" + id + "/progress-from-groups",
                method: "post",
                requiresAuth: true,
            }
        },
        clearKnockoutStages: (id) => {
            return {
                path: "/api/v2/tournaments/" + id + "/clear-knockout-stages",
                method: "post",
                requiresAuth: true,
            }
        },
    },
    tournamentStages: {
        ...crudEndpoints("tournamentStages"),
    },
    leagueCycles: {
        ...crudEndpoints("leagueCycles"),
        rolloverPreview: (id, change) => {
            return {
                path: "/api/v2/leagueCycles/" + id + "/rollover-preview?change=" + change,
                method: "get",
                requiresAuth: true,
            }
        },
        // Streamed PDF (application/pdf), never stored. Club admins and super admins
        // only (403 otherwise). Fetch with the bearer token (useFileDownload.fetchFile).
        pdfFile: (id, {type = "table", juniorFullNames = false} = {}) => ({
            path: `/api/v2/leagueCycles/${id}/pdf/file?type=${type}&juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
        // Page images of the same PDF, for the export dialog's preview (never the PDF).
        pdfPreview: (id, {type = "table", juniorFullNames = false} = {}) => ({
            path: `/api/v2/leagueCycles/${id}/pdf/preview?type=${type}&juniorFullNames=${juniorFullNames === true}`,
            method: "get",
            requiresAuth: true,
        }),
    },
    leagueCycleDivisions: {
        ...crudEndpoints("leagueCycleDivisions"),
        leave: (leagueCycleDivisionId) => {
            return {
                path: "/api/v2/leagueCycleDivisions/" + leagueCycleDivisionId + "/leave",
                method: "delete",
                requiresAuth: true,
            }
        },
    },
    leagueDivisions: {
        ...crudEndpoints("leagueDivisions"),
        move: (divisionId, isUp) => {
            return {
                path: "/api/v2/leagueDivisions/" + divisionId + "/move/" + isUp,
                method: "get",
                requiresAuth: true,
            }
        },
    },
    fixtures: {
        ...crudEndpoints("fixtures"),
        export: {
            path: "/api/v2/fixtures/export",
            method: "get",
            requiresAuth: true,
        },
        clearResult: (id) => {
            return {
                path: "/api/v2/fixtures/" + id + "/result",
                method: "delete",
                requiresAuth: true,
            }
        },
        clearSchedule: (id) => {
            return {
                path: "/api/v2/fixtures/" + id + "/schedule",
                method: "delete",
                requiresAuth: true,
            }
        }
    },
    scoringSystems: {
        ...crudEndpoints("scoringSystems"),
    },
    fixtureEvents: {
        ...crudEndpoints("fixtureevents"),
        // Atomic rewrite of a fixture's SCORER set (goals/own-goals) — replaces
        // manual rows AND ended live-session goals together (they're one
        // interchangeable set); non-scorer live events (cards) survive.
        // Body: {fixtureId, events: [...]}.
        bulk: {
            path: "/api/v2/fixtureevents/bulk",
            method: "put",
            requiresAuth: true,
        },
        leaderboard: {
            path: "/api/v2/fixtureevents/leaderboard",
            method: "get",
            requiresAuth: true,
        },
    },
    liveScoringSessions: {
        // protocol endpoints (batched sync/finalise) are called via useScoringApi;
        // listed here for the endpoint inventory
        start: {
            path: "/api/v2/livescoringsessions",
            method: "post",
            requiresAuth: true,
        },
        getItem: (id) => {
            return {
                path: "/api/v2/livescoringsessions/" + id,
                method: "get",
                requiresAuth: true,
            }
        },
        mine: {
            path: "/api/v2/livescoringsessions/mine",
            method: "get",
            requiresAuth: true,
        },
        appendEvents: (id) => {
            return {
                path: "/api/v2/livescoringsessions/" + id + "/events",
                method: "post",
                requiresAuth: true,
            }
        },
        finalise: (id) => {
            return {
                path: "/api/v2/livescoringsessions/" + id + "/finalise",
                method: "post",
                requiresAuth: true,
            }
        },
        abandon: (id) => {
            return {
                path: "/api/v2/livescoringsessions/" + id + "/abandon",
                method: "post",
                requiresAuth: true,
            }
        },
    },
    sports: {
        ...crudEndpoints("sports"),
    },
    countries: {
        // The LIMIT IS IN THE PATH on purpose. This list is a fixed catalogue of
        // 250 that every caller wants in full, and the paged default is 10 — so
        // a caller passing no params silently received Afghanistan through to
        // Argentina and nothing else. That is not visibly broken: the country
        // simply never matched, the location field looked answered, and the
        // button sat dead with nothing on screen to explain it.
        //
        // Note the parameter is `limit`, NOT `perPage`. perPage is what comes
        // BACK in the response, and passing it is silently ignored.
        getItems: {
            path: "/api/v2/countries?limit=500",
            method: "get",
            requiresAuth: false,
        },
    },
    // Join settings — one panel per entity over its managed join link (JOIN_SETTINGS_PLAN.md §5)
    joinSettings: {
        get: (referenceType, referenceId) => ({
            path: `/api/v2/joinsettings/${referenceType}/${referenceId}`,
            method: "get",
            requiresAuth: true,
        }),
        update: (referenceType, referenceId) => ({
            path: `/api/v2/joinsettings/${referenceType}/${referenceId}`,
            method: "put",
            requiresAuth: true,
        }),
        regenerate: (referenceType, referenceId) => ({
            path: `/api/v2/joinsettings/${referenceType}/${referenceId}/regenerate`,
            method: "post",
            requiresAuth: true,
        }),
        pdf: (referenceType, referenceId) => ({
            path: `/api/v2/joinsettings/${referenceType}/${referenceId}/pdf`,
            method: "get",
            requiresAuth: true,
        }),
    },
    // Questions a member is asked about a competition (the API's CompetitionQuestions
    // registry). type: ladder, league, leagueCycle, tournament, meet, americano, americanoCycle.
    competitionQuestions: {
        pending: (type, id, joining = false) => ({
            path: `/api/v2/competitionQuestions/${type}/${id}` + (joining ? '?joining=true' : ''),
            method: "get",
            requiresAuth: true,
        }),
        answer: (type, id) => ({
            path: `/api/v2/competitionQuestions/${type}/${id}`,
            method: "put",
            requiresAuth: true,
        }),
        // An account-wide question as it stands, whatever the member answered
        // (Turn on in place, SQUASH_LEVELS_CARDS_PLAN.md). Saved through
        // auth.meServices, not answer().
        account: (key) => ({
            path: `/api/v2/competitionQuestions/account/${key}`,
            method: "get",
            requiresAuth: true,
        }),
    },
    // Placement waiting area (JOIN_SETTINGS_PLAN.md §6)
    pendingPlacements: {
        ...crudEndpoints("pendingPlacements"),
        place: {
            path: "/api/v2/pendingPlacements/place",
            method: "post",
            requiresAuth: true,
        },
        preferredPartners: (id) => ({
            path: `/api/v2/pendingPlacements/${id}/preferred-partners`,
            method: "put",
            requiresAuth: true,
        }),
        suggested: (referenceType, referenceId) => ({
            path: `/api/v2/pendingPlacements/suggested/${referenceType}/${referenceId}`,
            method: "get",
            requiresAuth: true,
        }),
    },
    inviteLinks: {
        ...crudEndpoints("inviteLinks"),
        getBySlug: (slug) => {
            return {
                path: "/api/v2/inviteLinks/" + slug,
                method: "get",
                requiresAuth: false,
            }
        },
        join: (id) => {
            return {
                path: "/api/v2/inviteLinks/" + id + "/join",
                method: "post",
                requiresAuth: true,
            }
        },
        unlock: (id) => {
            return {
                path: "/api/v2/inviteLinks/" + id + "/unlock",
                method: "post",
                requiresAuth: false,
            }
        },
        pdf: (id) => {
            return {
                path: "/api/v2/inviteLinks/" + id + "/pdf",
                method: "get",
                requiresAuth: true,
            }
        },
    },
    playingAreas: {
        ...crudEndpoints("playingAreas"),
    },
    memberImports: {
        // POST /api/v2/memberImports is multipart (file + clubId) — sent with
        // FormData directly from the import page, not through these helpers.
        get: (id) => {
            return {
                path: "/api/v2/memberImports/" + id,
                method: "get",
                requiresAuth: true,
            }
        },
        updateMapping: (id) => {
            return {
                path: "/api/v2/memberImports/" + id + "/mapping",
                method: "put",
                requiresAuth: true,
            }
        },
        updateRow: (id, rowId) => {
            return {
                path: "/api/v2/memberImports/" + id + "/rows/" + rowId,
                method: "put",
                requiresAuth: true,
            }
        },
        process: (id) => {
            return {
                path: "/api/v2/memberImports/" + id + "/process",
                method: "post",
                requiresAuth: true,
            }
        },
        report: (id) => {
            return {
                path: "/api/v2/memberImports/" + id + "/report",
                method: "get",
                requiresAuth: true,
            }
        },
    },
    widgets: {
        ...crudEndpoints("widgets"),
        regenerateKey: (id) => {
            return {
                path: "/api/v2/widgets/" + id + "/regenerate-key",
                method: "post",
                requiresAuth: true,
            }
        },
        getEmbed: (publicKey) => {
            return {
                path: "/api/v2/widgets/embed/" + publicKey,
                method: "get",
                requiresAuth: false,
            }
        },
    },
    // Clubhouse TV screens (public view at /tv/{publicKey})
    clubDisplays: {
        ...crudEndpoints("clubDisplays"),
        regenerateKey: (id) => {
            return {
                path: "/api/v2/clubDisplays/" + id + "/regenerate-key",
                method: "post",
                requiresAuth: true,
            }
        },
        panels: (clubId, displayId, leagueView) => {
            return {
                path: "/api/v2/clubDisplays/panels?clubId=" + clubId + (displayId ? "&displayId=" + displayId : "")
                    + (leagueView != null ? "&leagueView=" + leagueView : ""),
                method: "get",
                requiresAuth: true,
            }
        },
    },
    // Super admin: combine two accounts that belong to one person (ACCOUNT_IDENTITY_PLAN.md)
    accountMerges: {
        suspects: () => ({path: "/api/v2/admin/accountMerges/suspects", method: "get", requiresAuth: true}),
        search: (query) => ({path: "/api/v2/admin/accountMerges/users?search=" + encodeURIComponent(query), method: "get", requiresAuth: true}),
        preview: () => ({path: "/api/v2/admin/accountMerges/preview", method: "post", requiresAuth: true}),
        merge: () => ({path: "/api/v2/admin/accountMerges", method: "post", requiresAuth: true}),
    },
    playerMerges: {
        ...crudEndpoints("playerMerges"),
        canReverse: (id) => {
            return {
                path: "/api/v2/playerMerges/" + id + "/can-reverse",
                method: "get",
                requiresAuth: true,
            }
        },
        reverse: (id) => {
            return {
                path: "/api/v2/playerMerges/" + id + "/reverse",
                method: "post",
                requiresAuth: true,
            }
        },
    },
    onlineUsers: {
        list: {path: "/api/v2/onlineUsers", method: "get", requiresAuth: true},
    },
    assistant: {
        // chat is SSE — streamed directly in useAssistant.js, not via useHttpRequest
        chat: {path: "/api/v2/assistant/chat", method: "post", requiresAuth: true},
        rateMessage: (id) => {
            return {
                path: "/api/v2/assistant/messages/" + id + "/rating",
                method: "put",
                requiresAuth: true,
            }
        },
        currentConversation: {path: "/api/v2/assistant/conversations/current", method: "get", requiresAuth: true},
        closeConversation: {path: "/api/v2/assistant/conversations/current/close", method: "post", requiresAuth: true},
    },
    // Online Safety Act: report a message or conversation; block a member.
    contentReports: {
        create: {path: "/api/v2/contentReports", method: "post", requiresAuth: true},
        // Who will read a report of this message (or conversation), said before it is sent.
        audience: {path: "/api/v2/contentReports/audience", method: "get", requiresAuth: true},
        list: {path: "/api/v2/contentReports", method: "get", requiresAuth: true},
        summary: {path: "/api/v2/contentReports/summary", method: "get", requiresAuth: true},
        // Super admin only: reporting statistics for the whole system, no message text.
        stats: {path: "/api/v2/contentReports/stats", method: "get", requiresAuth: true},
        review: (id) => ({path: `/api/v2/contentReports/${id}/review`, method: "put", requiresAuth: true}),
    },
    // Follow a player (PRIVACY_PLAN.md §3.6)
    userFollows: {
        list: {path: "/api/v2/userFollows", method: "get", requiresAuth: true},
        // By username only (UserTagRules): exact, so nobody can list the members.
        search: {path: "/api/v2/userFollows/search", method: "get", requiresAuth: true},
        setTag: {path: "/api/v2/userFollows/tag", method: "put", requiresAuth: true},
        suggestTag: {path: "/api/v2/userFollows/tag/suggest", method: "get", requiresAuth: true},
        skipTag: {path: "/api/v2/userFollows/tag/skip", method: "post", requiresAuth: true},
        tagAvailable: {path: "/api/v2/userFollows/tag/available", method: "get", requiresAuth: true},
        pendingForClubs: {path: "/api/v2/userFollows/pendingForClubs", method: "get", requiresAuth: true},
        request: {path: "/api/v2/userFollows", method: "post", requiresAuth: true},
        accept: (id) => ({path: `/api/v2/userFollows/${id}/accept`, method: "post", requiresAuth: true}),
        decline: (id) => ({path: `/api/v2/userFollows/${id}/decline`, method: "post", requiresAuth: true}),
        remove: (id) => ({path: `/api/v2/userFollows/${id}`, method: "delete", requiresAuth: true}),
    },
    userBlocks: {
        list: {path: "/api/v2/userBlocks", method: "get", requiresAuth: true},
        block: (userId) => ({path: `/api/v2/userBlocks/${userId}`, method: "put", requiresAuth: true}),
        unblock: (userId) => ({path: `/api/v2/userBlocks/${userId}`, method: "delete", requiresAuth: true}),
    },
    assistantNotice: {
        // Super admin only — the monitoring/data-use notice shown in every chat.
        get: {path: "/api/v2/assistantNotice", method: "get", requiresAuth: true},
        update: {path: "/api/v2/assistantNotice", method: "put", requiresAuth: true},
    },
    assistantAnalytics: {
        summary: {path: "/api/v2/assistantAnalytics/summary", method: "get", requiresAuth: true},
        daily: {path: "/api/v2/assistantAnalytics/daily", method: "get", requiresAuth: true},
        conversations: {path: "/api/v2/assistantAnalytics/conversations", method: "get", requiresAuth: true},
        transcript: (id) => {
            return {
                path: "/api/v2/assistantAnalytics/conversations/" + id,
                method: "get",
                requiresAuth: true,
            }
        },
        // Streams a JSON file (aggregates + every transcript in the window) —
        // use useFileDownload, not httpRequest.
        export: {path: "/api/v2/assistantAnalytics/export", method: "get", requiresAuth: true},
    },
    // Assistant eval console (super admin, and only where AssistantEvals:Enabled is on:
    // a 404 from any of these means switched off). dryRun rides in the PATH on the PUT
    // and POST ones, because httpRequest only builds a query string for GET.
    assistantEvals: (() => {
        const base = "/api/v2/assistantEvals";
        const at = (path, method = "get") => ({path: base + path, method, requiresAuth: true});
        const dry = (dryRun) => "?dryRun=" + (dryRun ? "true" : "false");
        const id = (value) => encodeURIComponent(value);
        return {
            status: at("/status"),
            cases: at("/cases"),
            // trialGroup: put every pasted case into that trial group (omit to keep what each case says).
            putCases: (dryRun, trialGroup) => at("/cases" + dry(dryRun) +
                (trialGroup ? "&trialGroup=" + id(trialGroup) : ""), "put"),
            moveCases: at("/cases/move", "post"),
            trialGroups: at("/trialGroups"),
            addTrialGroupToSuite: at("/trialGroups/addToSuite", "post"),
            deleteTrialGroup: at("/trialGroups/delete", "post"),
            deleteCase: (caseId) => at("/cases/" + id(caseId), "delete"),
            importCases: (dryRun) => at("/cases/importFromRepo" + dry(dryRun), "post"),
            // Streams cases.json: use useFileDownload, not httpRequest.
            exportCases: at("/cases/export"),
            coverage: at("/coverage"),
            // Suite cases whose docs differ between the latest prod release and what is published here.
            differ: at("/differ"),
            estimate: at("/estimate", "post"),
            startRun: at("/runs", "post"),
            runs: at("/runs"),
            run: (runId) => at("/runs/" + id(runId)),
            cancelRun: (runId) => at("/runs/" + id(runId) + "/cancel", "post"),
            resumeRun: (runId) => at("/runs/" + id(runId) + "/resume", "post"),
            analysis: (runId) => at("/runs/" + id(runId) + "/analysis"),
            docsFindings: (runId) => at("/runs/" + id(runId) + "/docsFindings"),
            compare: at("/runs/compare"),
            judgeChecks: at("/judgeChecks"),
            putJudgeChecks: (dryRun) => at("/judgeChecks" + dry(dryRun), "put"),
            importJudgeChecks: (dryRun) => at("/judgeChecks/importFromRepo" + dry(dryRun), "post"),
            deleteJudgeCheck: (itemId) => at("/judgeChecks/" + id(itemId), "delete"),
            runJudgeCheck: at("/judgeChecks/run", "post"),
        };
    })(),
    // ── Payments (Stripe Connect) — SLL.Api V2 payment controllers ──
    payments: {
        // PaymentMethodsController — the calling user's saved cards (platform-level)
        setupIntent: {path: "/api/v2/paymentMethods/setupIntent", method: "post", requiresAuth: true},
        myPaymentMethods: {path: "/api/v2/paymentMethods/mine", method: "get", requiresAuth: true},
        setDefaultPaymentMethod: (id) => {
            return {path: "/api/v2/paymentMethods/" + id, method: "put", requiresAuth: true}
        },
        deletePaymentMethod: (id) => {
            return {path: "/api/v2/paymentMethods/" + id, method: "delete", requiresAuth: true}
        },
        // What a club would receive, split as real charges are (PaymentsController, Payments
        // plan v3 phase 2). Query: { clubId, amountMinor, count } or { clubId, bundleMinor: [..] },
        // optional currency, meetId, commissionPercent (super admins only). Club admins.
        estimate: {path: "/api/v2/payments/estimate", method: "get", requiresAuth: true},
        // PaymentChargesController — history + recovery
        charges: {path: "/api/v2/paymentCharges", method: "get", requiresAuth: true},
        // What WILL hit my card and when — bundled like the billing runs bundle it
        upcomingCharges: {path: "/api/v2/paymentCharges/upcoming", method: "get", requiresAuth: true},
        charge: (id) => {
            return {path: "/api/v2/paymentCharges/" + id, method: "get", requiresAuth: true}
        },
        resolveCharge: (id) => {
            // body: { resolution: 0 SettleOffline | 1 Waive }
            return {path: "/api/v2/paymentCharges/" + id, method: "put", requiresAuth: true}
        },
        retryCharge: (id) => {
            return {path: "/api/v2/paymentCharges/" + id + "/retry", method: "post", requiresAuth: true}
        },
        payCharge: (id) => {
            // body: { userPaymentMethodId }
            return {path: "/api/v2/paymentCharges/" + id + "/pay", method: "post", requiresAuth: true}
        },
        chargeReceipt: (id) => {
            // streams a PDF — use useFileDownload, not httpRequest
            return {path: "/api/v2/paymentCharges/" + id + "/receipt", method: "get", requiresAuth: true}
        },
        // streams an Excel statement — query: { clubId, from, to, sourceType?, sourceId? }
        chargeStatement: {path: "/api/v2/paymentCharges/statement", method: "get", requiresAuth: true},
        // Aggregated club figures for the dashboard tiles + tab badges — query:
        // { clubId, from?, to? }. Summed in SQL so the tiles never depend on how
        // many charge rows the page happened to load.
        chargeSummary: {path: "/api/v2/paymentCharges/summary", method: "get", requiresAuth: true},
        // PaymentRefundsController — club admin only
        createRefund: {path: "/api/v2/paymentRefunds", method: "post", requiresAuth: true},
        // EntryFeeObligationsController — competition "fees due" ledger
        myEntryFeeObligations: {path: "/api/v2/entryFeeObligations/mine", method: "get", requiresAuth: true},
        competitionEntryFeeObligations: {path: "/api/v2/entryFeeObligations/competition", method: "get", requiresAuth: true},
        waiveEntryFeeObligation: (id) => {
            return {path: "/api/v2/entryFeeObligations/" + id + "/waive", method: "post", requiresAuth: true}
        },
        unwaiveEntryFeeObligation: (id) => {
            return {path: "/api/v2/entryFeeObligations/" + id + "/unwaive", method: "post", requiresAuth: true}
        },
        // Present the fee to another club member (under-18s, guests, admin-managed
        // players) instead of the team — body {assignedUserId}, null clears
        assignObligation: (id) => {
            return {path: "/api/v2/entryFeeObligations/" + id + "/assign", method: "post", requiresAuth: true}
        },
        generateEntryFeePayLink: (id) => {
            return {path: "/api/v2/entryFeeObligations/" + id + "/payLink", method: "post", requiresAuth: true}
        },
        clubEntryFeeObligations: (clubId) => {
            return {path: "/api/v2/entryFeeObligations/club?clubId=" + clubId, method: "get", requiresAuth: true}
        },
        // Every competition in the club with ANY entry-fee obligation, rolled up
        // (due/paid/waived counts + amounts). ONE call drives the conditional
        // Payments action on the ladders/leagues/tournaments list pages —
        // obligations are polymorphic, so this can't ride on the list responses.
        clubEntryFeeSummaries: (clubId) => {
            return {
                path: "/api/v2/entryFeeObligations/club/summaries?clubId=" + clubId,
                method: "get", requiresAuth: true
            }
        },
        disconnectClubPaymentAccount: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/disconnect", method: "post", requiresAuth: true}
        },
        // A club's EU VAT number (Payments plan v3 phase 4): the card, saving it (checked with
        // VIES; refusals carry {global, code}) and removing it.
        clubVatNumber: (clubId) => ({path: "/api/v2/clubPaymentAccounts/" + clubId + "/vatNumber", method: "get", requiresAuth: true}),
        saveClubVatNumber: (clubId) => ({path: "/api/v2/clubPaymentAccounts/" + clubId + "/vatNumber", method: "put", requiresAuth: true}),
        removeClubVatNumber: (clubId) => ({path: "/api/v2/clubPaymentAccounts/" + clubId + "/vatNumber", method: "delete", requiresAuth: true}),
        // Our VAT invoices and credit notes to a club (Payments plan v3 phase 5): the list (club
        // admins and super admins), and one as a PDF, streamed: use useFileDownload, not httpRequest.
        vatInvoices: (clubId) => ({path: "/api/v2/vatInvoices/club/" + clubId, method: "get", requiresAuth: true}),
        vatInvoicePdf: (id) => ({path: "/api/v2/vatInvoices/" + id + "/pdf", method: "get", requiresAuth: true}),
        clubFinancials: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/financials", method: "get", requiresAuth: true}
        },
        updateClubPayoutSchedule: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/payoutSchedule", method: "put", requiresAuth: true}
        },
        createClubPayout: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/payouts", method: "post", requiresAuth: true}
        },
        // Anonymous — the token IS the authorisation (/pay/{token} page)
        entryFeeTerms: (sourceType, sourceId) => {
            return {path: "/api/v2/entryFeeObligations/terms?sourceType=" + sourceType + "&sourceId=" + sourceId, method: "get", requiresAuth: true}
        },
        setEntryFeeTerms: {path: "/api/v2/entryFeeObligations/terms", method: "put", requiresAuth: true},
        // Admin previews before saving: who a terms edit or a new fee asks again, and who is only told.
        entryFeeTermsChangeImpact: (sourceType, sourceId) => ({
            path: "/api/v2/entryFeeObligations/termsChangeImpact?sourceType=" + encodeURIComponent(sourceType) + "&sourceId=" + sourceId,
            method: "get", requiresAuth: true,
        }),
        entryFeeChangeImpact: (competition, competitionId, feeMinor, currency) => ({
            path: "/api/v2/entryFeeObligations/feeChangeImpact?competition=" + competition + "&competitionId=" + competitionId
                + (feeMinor == null ? "" : "&feeMinor=" + feeMinor) + (currency ? "&currency=" + encodeURIComponent(currency) : ""),
            method: "get", requiresAuth: true,
        }),
        entryFeePayLink: (token) => {
            return {path: "/api/v2/entryFeeObligations/payLink/" + token, method: "get", requiresAuth: false}
        },
        entryFeePayLinkCheckout: (token) => {
            return {path: "/api/v2/entryFeeObligations/payLink/" + token + "/checkout", method: "post", requiresAuth: false}
        },
        // Charge pay links — an admin mints a no-login link for an outstanding
        // charge; returns {token}, public page is /pay/charge/{token}
        generateChargePayLink: (id) => {
            return {path: "/api/v2/paymentCharges/" + id + "/payLink", method: "post", requiresAuth: true}
        },
        // Signed-in payer (any member) settles a pay-link charge with one of THEIR saved cards
        chargePayLinkPaySaved: (token) => {
            // body: { userPaymentMethodId }
            return {path: "/api/v2/paymentCharges/payLink/" + token + "/paySaved", method: "post", requiresAuth: true}
        },
        // Anonymous — the token IS the authorisation (/pay/charge/{token} page)
        chargePayLink: (token) => {
            return {path: "/api/v2/paymentCharges/payLink/" + token, method: "get", requiresAuth: false}
        },
        chargePayLinkCheckout: (token) => {
            return {path: "/api/v2/paymentCharges/payLink/" + token + "/checkout", method: "post", requiresAuth: false}
        },
        checkoutEntryFeeObligation: (id) => {
            return {path: "/api/v2/entryFeeObligations/" + id + "/checkout", method: "post", requiresAuth: true}
        },
        payEntryFeeObligationWithSavedMethod: (id) => {
            // body: { userPaymentMethodId } (null = default card)
            return {path: "/api/v2/entryFeeObligations/" + id + "/payWithSavedMethod", method: "post", requiresAuth: true}
        },
        // ClubPaymentAccountsController — club Stripe onboarding
        clubPaymentAccount: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId, method: "get", requiresAuth: true}
        },
        createClubPaymentAccount: {path: "/api/v2/clubPaymentAccounts", method: "post", requiresAuth: true},
        // The countries a club's payment account can be opened in, each
        // available or comingSoon; one not listed is not available (23 Oct 2026).
        clubPaymentCountries: {path: "/api/v2/clubPaymentAccounts/countries", method: "get", requiresAuth: true},
        // A club admin registers interest in a country payments aren't open in.
        // body: {country}. Idempotent per club and country.
        registerCountryInterest: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/countryInterest", method: "post", requiresAuth: true}
        },
        // The club's commission if its payments were in `country` (club admins):
        // {percent, label, sourceLine, scheduledLine}. For the setup dialog's
        // rate line, before the account exists (28 Oct 2026).
        clubCommission: (clubId, country) => {
            const query = country ? "?country=" + encodeURIComponent(country) : "";
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/commission" + query, method: "get", requiresAuth: true}
        },
        // Super admin: clubs that registered interest, per country (already ordered).
        countryInterestSummary: {path: "/api/v2/clubPaymentAccounts/countryInterest", method: "get", requiresAuth: true},
        // Account Session client secret for the Connect embedded components
        // (onboarding, notification banner, account management, payouts) —
        // connect-js calls this on mount and again whenever the session expires.
        clubAccountSession: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/accountSession", method: "post", requiresAuth: true}
        },
        cancelClubOnboarding: (clubId) => {
            return {path: "/api/v2/clubPaymentAccounts/" + clubId + "/cancel", method: "post", requiresAuth: true}
        },
    },
    // The payments prompts for club admins (Release 1c, useEntryFeePrompt.js): what a fee
    // could bring in, for one wizard step or a whole list page, and the How it works points.
    // Admins of the club and super admins; refusals {global, code}.
    paymentPrompts: {
        // body: {clubId, items: [{key, kind, competitionId?, playerCount?, feeMinor?, playersPerTeam?, meet?}]}
        figures: {path: "/api/v2/paymentPrompts/figures", method: "post", requiresAuth: true},
        // query: {clubId, feeMinor?, kind?}
        howItWorks: {path: "/api/v2/paymentPrompts/howItWorks", method: "get", requiresAuth: true},
    },
    superAnalytics: {
        summary: {path: "/api/v2/superAnalytics/summary", method: "get", requiresAuth: true},
        growth: {path: "/api/v2/superAnalytics/growth", method: "get", requiresAuth: true},
        formatComparison: {path: "/api/v2/superAnalytics/formatComparison", method: "get", requiresAuth: true},
        sportComparison: {path: "/api/v2/superAnalytics/sportComparison", method: "get", requiresAuth: true},
        clubHealth: {path: "/api/v2/superAnalytics/clubHealth", method: "get", requiresAuth: true},
        // Feature adoption as of now (no window) — super only
        adoption: {path: "/api/v2/superAnalytics/adoption", method: "get", requiresAuth: true},
        // C2 platform payments overview — ?month=yyyy-MM (super only)
        payments: {path: "/api/v2/superAnalytics/payments", method: "get", requiresAuth: true},
        // Monthly buckets for the trend charts — ?months=12 (super only)
        paymentsSeries: {path: "/api/v2/superAnalytics/payments/series", method: "get", requiresAuth: true},
    },
    // Club-organiser journey — where new signups get to, how long each step takes,
    // and who is stuck (super only). Feeds /admin/super/journey.
    userJourney: {
        funnel: {path: "/api/v2/userJourney/funnel", method: "get", requiresAuth: true},
        curves: {path: "/api/v2/userJourney/curves", method: "get", requiresAuth: true},
        retention: {path: "/api/v2/userJourney/retention", method: "get", requiresAuth: true},
        insights: {path: "/api/v2/userJourney/insights", method: "get", requiresAuth: true},
        stalled: {path: "/api/v2/userJourney/stalled", method: "get", requiresAuth: true},
        // ActivityCounts and ActivityRollupDays, the anonymous totals kept before the log is purged.
        activity: {path: "/api/v2/userJourney/activity", method: "get", requiresAuth: true},
    },
    // Organiser outreach. The pre v1 stats, which the journey Activity tab reads. Super admin only.
    outreach: {
        stats: {path: "/api/v2/outreach/stats", method: "get", requiresAuth: true},
    },
    // Outreach v1 templates, house style, voices and the one global Publish
    // (OUTREACH-V1-TDD-PLAN.md checkpoint 10). Super admin only; open while sending is paused.
    outreachTemplates: {
        list: {path: "/api/v2/outreachTemplates", method: "get", requiresAuth: true},
        detail: (id) => ({path: "/api/v2/outreachTemplates/" + id, method: "get", requiresAuth: true}),
        draft: (id) => ({path: "/api/v2/outreachTemplates/" + id + "/draft", method: "post", requiresAuth: true}),
        edit: (id) => ({path: "/api/v2/outreachTemplates/" + id + "/edit", method: "post", requiresAuth: true}),
        approve: (versionId) => ({path: "/api/v2/outreachTemplates/versions/" + versionId + "/approve", method: "post", requiresAuth: true}),
        addToMyVoice: (versionId) => ({path: "/api/v2/outreachTemplates/versions/" + versionId + "/addToMyVoice", method: "post", requiresAuth: true}),
        preview: (versionId) => ({path: "/api/v2/outreachTemplates/versions/" + versionId + "/preview", method: "get", requiresAuth: true}),
        sendTest: (versionId) => ({path: "/api/v2/outreachTemplates/versions/" + versionId + "/sendTest", method: "post", requiresAuth: true}),
        samples: {path: "/api/v2/outreachTemplates/samples", method: "get", requiresAuth: true},
        pending: {path: "/api/v2/outreachTemplates/pending", method: "get", requiresAuth: true},
        publish: {path: "/api/v2/outreachTemplates/publish", method: "post", requiresAuth: true},
        publishes: {path: "/api/v2/outreachTemplates/publishes", method: "get", requiresAuth: true},
        guidance: {path: "/api/v2/outreachTemplates/guidance", method: "get", requiresAuth: true},
        saveGuidance: {path: "/api/v2/outreachTemplates/guidance", method: "put", requiresAuth: true},
        proposeGuidance: {path: "/api/v2/outreachTemplates/guidance/propose", method: "post", requiresAuth: true},
        voices: {path: "/api/v2/outreachTemplates/voices", method: "get", requiresAuth: true},
        saveMyVoice: {path: "/api/v2/outreachTemplates/voices/mine", method: "put", requiresAuth: true},
    },
    // Outreach v1 Results (checkpoint 11): counts only, and the Replied mark by address. Super admin only.
    outreachResults: {
        get: {path: "/api/v2/outreachResults", method: "get", requiresAuth: true},
        replied: {path: "/api/v2/outreachResults/replied", method: "post", requiresAuth: true},
        undoReplied: {path: "/api/v2/outreachResults/replied/undo", method: "post", requiresAuth: true},
    },
    // Outreach Turn on and Pause (owner, 28 Oct 2026): the state, who changed it and when. Super admin only.
    outreachSwitch: {
        get: {path: "/api/v2/outreachSwitch", method: "get", requiresAuth: true},
        set: {path: "/api/v2/outreachSwitch", method: "put", requiresAuth: true},
    },
    // End of the outreach endpoints
    // Super-admin payments platform controls (MEETS_SCREENS.md C1/C3/C4 + P20)
    superPayments: {
        // The platform default commission. A rise starts no earlier than 15 days after the email
        // (startsOn); preview says what a change would do before it is saved; DELETE scheduled
        // withdraws a rise still to come ({emailAdmins}).
        commission: {path: "/api/v2/platformCommission", method: "get", requiresAuth: true},
        updateCommission: {path: "/api/v2/platformCommission", method: "put", requiresAuth: true},
        previewCommission: {path: "/api/v2/platformCommission/preview", method: "post", requiresAuth: true},
        withdrawCommission: {path: "/api/v2/platformCommission/scheduled", method: "delete", requiresAuth: true},
        // Countries and commission (28 Oct 2026): each listed country with its rate, counts and
        // status, the deals, and the changes. Refusals are {global:[message], code}.
        paymentCountries: {path: "/api/v2/paymentCountries", method: "get", requiresAuth: true},
        paymentCountryHistory: (iso) => ({
            path: "/api/v2/paymentCountries/" + encodeURIComponent(iso) + "/history", method: "get", requiresAuth: true,
        }),
        previewCountryCommission: (iso) => ({
            path: "/api/v2/paymentCountries/" + encodeURIComponent(iso) + "/commission/preview", method: "post", requiresAuth: true,
        }),
        setCountryCommission: (iso) => ({
            path: "/api/v2/paymentCountries/" + encodeURIComponent(iso) + "/commission", method: "put", requiresAuth: true,
        }),
        withdrawCountryCommission: (iso) => ({
            path: "/api/v2/paymentCountries/" + encodeURIComponent(iso) + "/commission/scheduled", method: "delete", requiresAuth: true,
        }),
        addPaymentCountry: {path: "/api/v2/paymentCountries", method: "post", requiresAuth: true},
        setPaymentCountryStatus: (iso) => ({
            path: "/api/v2/paymentCountries/" + encodeURIComponent(iso) + "/status", method: "put", requiresAuth: true,
        }),
        // C3 — connected accounts oversight + the P20 kill switch
        accounts: {path: "/api/v2/clubPaymentAccounts", method: "get", requiresAuth: true},
        suspendAccount: (id) => {
            return {path: "/api/v2/clubPaymentAccounts/" + id + "/suspend", method: "post", requiresAuth: true}
        },
        unsuspendAccount: (id) => {
            return {path: "/api/v2/clubPaymentAccounts/" + id + "/unsuspend", method: "post", requiresAuth: true}
        },
        // C4 — versioned sample T&Cs
        termsTemplates: {path: "/api/v2/termsTemplates", method: "get", requiresAuth: true},
        latestTermsTemplate: {path: "/api/v2/termsTemplates/latest", method: "get", requiresAuth: true},
        publishTermsTemplate: {path: "/api/v2/termsTemplates", method: "post", requiresAuth: true},
        // The VAT switchboard (Payments plan v3 phase 3)
        vatSwitchboard: {path: "/api/v2/vatSwitchboard", method: "get", requiresAuth: true},
        updateVatUk: {path: "/api/v2/vatSwitchboard/uk", method: "put", requiresAuth: true},
        updateVatEu: {path: "/api/v2/vatSwitchboard/eu", method: "put", requiresAuth: true},
        addVatRate: {path: "/api/v2/vatSwitchboard/rates", method: "post", requiresAuth: true},
        // The VAT report for our returns (Payments plan v3 phase 6). GET with ?period= (UK, reverse
        // charge) or ?quarter= (EU); the three CSVs go through useFileDownload.
        vatReportPeriods: {path: "/api/v2/vatReport/periods", method: "get", requiresAuth: true},
        vatReportUk: {path: "/api/v2/vatReport/uk", method: "get", requiresAuth: true},
        vatReportOss: {path: "/api/v2/vatReport/oss", method: "get", requiresAuth: true},
        vatReportReverseCharge: {path: "/api/v2/vatReport/reverseCharge", method: "get", requiresAuth: true},
        vatReportUkCsv: {path: "/api/v2/vatReport/uk/csv", method: "get", requiresAuth: true},
        vatReportOssCsv: {path: "/api/v2/vatReport/oss/csv", method: "get", requiresAuth: true},
        vatReportReverseChargeCsv: {path: "/api/v2/vatReport/reverseCharge/csv", method: "get", requiresAuth: true},
    },

    places: {
        // The region search, through our API exactly as the app does it, so
        // the rule for what a pick becomes lives once, server-side. One
        // session token per chosen place, URL-encoded: an unencoded "#" once
        // ended the app's query string and dropped regionsOnly.
        //
        // `scope` is the field's placeScope, decided by the server (the binding,
        // today always "regions"), so a later scope needs no web release. With
        // none, regionsOnly=true, which the server resolves to the same thing.
        //
        // `locale` is the device's language ("en-GB"): the server leans the
        // results towards its region, so "Kent" finds Kent, England first
        // (29 Sep 2026). A lean, never a filter; left out when unknown.
        regionAutocomplete: (query, sessionToken, scope, locale) => {
            return {
                path: "/api/v2/places/autocomplete?query=" + encodeURIComponent(query) +
                    "&" + placeScopeParam(scope) + "&sessionToken=" + encodeURIComponent(sessionToken ?? "") +
                    (locale ? "&locale=" + encodeURIComponent(locale) : ""),
                method: "get",
                requiresAuth: true,
            }
        },
        regionDetails: (placeId, sessionToken, scope) => {
            return {
                path: "/api/v2/places/" + encodeURIComponent(placeId) +
                    "?" + placeScopeParam(scope) + "&sessionToken=" + encodeURIComponent(sessionToken ?? ""),
                method: "get",
                requiresAuth: true,
            }
        },
        // The county or region a device position falls in, plus THAT REGION's
        // centre. The position is spent on the question and never stored; what
        // comes back describes a county, not a person.
        //
        // Through our own API rather than the Maps SDK so the web and the app
        // answer the button identically. The app's geocoding package returns
        // names with no coordinates at all, so it could name a county and never
        // place it, and two implementations of one button is how they drift.
        reverse: (latitude, longitude) => {
            return {
                path: "/api/v2/places/reverse?latitude=" + encodeURIComponent(latitude) +
                    "&longitude=" + encodeURIComponent(longitude),
                method: "get",
                requiresAuth: true,
            }
        },
    }
}
